import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight, Clock, ArrowLeft, Tag, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { API } from "@/lib/constants";

const DEFAULT_IMAGE = "https://customer-assets.emergentagent.com/job_e227742f-fa2d-44b4-a974-4045826f8a41/artifacts/epvouypm_DMN%20Logo%20Final%20A%20%28Light%29%20transparent.PNG";

const BlogCategoryPage = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentCategory, setCurrentCategory] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [postsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/blog`),
          axios.get(`${API}/blog/categories`)
        ]);
        
        const allPosts = postsRes.data;
        const allCategories = categoriesRes.data;
        
        setCategories(allCategories);
        
        // Find the current category
        const cat = allCategories.find(c => c.slug === category);
        setCurrentCategory(cat);
        
        // Filter posts by category
        if (cat) {
          const filteredPosts = allPosts.filter(
            p => p.category.toLowerCase().replace(/\s+/g, '-').replace('&', 'and') === category ||
                 p.category === cat.name
          );
          setPosts(filteredPosts);
        } else {
          setPosts(allPosts);
        }
      } catch (error) {
        console.error("Error fetching blog data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [category]);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-[#00E676] animate-pulse">Loading...</div>
      </div>
    );
  }

  const categoryName = currentCategory?.name || category?.replace(/-/g, ' ').replace('and', '&');
  const categoryDescription = currentCategory?.description || `Explore our ${categoryName} articles with expert insights on digital marketing strategies for Dallas businesses.`;

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="blog-category-page">
      <Helmet>
        <title>{`${categoryName} Articles | Digital Marketing Blog | Digital Media Ninja`}</title>
        <meta name="description" content={categoryDescription} />
        <link rel="canonical" href={`https://digitalmedianinja.com/blog/category/${category}`} />
        <meta property="og:title" content={`${categoryName} - Digital Media Ninja Blog`} />
        <meta property="og:description" content={categoryDescription} />
        <meta property="og:image" content={DEFAULT_IMAGE} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* Breadcrumb */}
      <div className="container-custom mb-8">
        <nav className="flex items-center gap-2 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#00E676]">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/blog" className="hover:text-[#00E676]">Blog</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-400 capitalize">{categoryName}</span>
        </nav>
      </div>

      {/* Header */}
      <div className="container-custom mb-12">
        <Button 
          variant="ghost" 
          className="mb-4 text-gray-400 hover:text-white"
          onClick={() => navigate("/blog")}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          All Posts
        </Button>
        
        <div className="flex items-center gap-4 mb-4">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00E676]/20 text-[#00E676]">
            <Tag className="w-4 h-4" />
            <span className="font-medium capitalize">{categoryName}</span>
          </span>
          <span className="text-gray-500">
            {posts.length} {posts.length === 1 ? 'article' : 'articles'}
          </span>
        </div>
        
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          <span className="capitalize">{categoryName}</span> Articles
        </h1>
        <p className="text-xl text-gray-400 max-w-2xl">
          Expert insights and guides on {categoryName?.toLowerCase()} to help your Dallas business grow.
        </p>
      </div>

      {/* Category Filter Pills */}
      <div className="container-custom mb-12">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-500">Browse by category:</span>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/blog">
            <Button 
              variant="outline"
              className="btn-outline"
            >
              All Posts
            </Button>
          </Link>
          {categories.map((cat) => (
            <Link key={cat.slug} to={`/blog/category/${cat.slug}`}>
              <Button 
                variant={cat.slug === category ? "default" : "outline"}
                className={cat.slug === category ? "btn-ninja" : "btn-outline"}
              >
                {cat.name} ({cat.count})
              </Button>
            </Link>
          ))}
        </div>
      </div>

      {/* Posts Grid */}
      <div className="container-custom">
        {posts.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-gray-400 text-lg mb-4">No articles found in this category.</p>
            <Link to="/blog">
              <Button className="btn-ninja">View All Articles</Button>
            </Link>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="container-custom mt-16">
        <div className="bg-gradient-to-r from-[#00E676]/10 to-[#6366F1]/10 rounded-2xl p-12 text-center">
          <h2 className="text-3xl font-bold mb-4">Want to Learn More?</h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Get a free consultation and discover how we can help your Dallas business succeed with {categoryName?.toLowerCase()}.
          </p>
          <Link to="/contact">
            <Button className="btn-ninja px-8">Get Free Consultation</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

const BlogCard = ({ post }) => (
  <Card className="bg-[#0A0A0A] border-white/10 hover:border-[#00E676]/30 transition-all overflow-hidden group">
    <div className="relative h-48">
      <img 
        src={post.featured_image}
        alt={post.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        onError={(e) => {
          e.target.src = "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800";
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
      <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-[#00E676]/20 text-[#00E676] text-sm">
        {post.category}
      </span>
    </div>
    <CardContent className="p-6">
      <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
        <span className="flex items-center gap-1">
          <Clock className="w-4 h-4" />
          {post.read_time} min read
        </span>
      </div>
      <h3 className="text-xl font-bold mb-3 group-hover:text-[#00E676] transition-colors line-clamp-2">
        {post.title}
      </h3>
      <p className="text-gray-400 text-sm mb-4 line-clamp-2">
        {post.excerpt}
      </p>
      <Link to={`/blog/${post.slug}`}>
        <Button variant="ghost" className="text-[#00E676] hover:text-[#00C853] p-0">
          Read Article <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </Link>
    </CardContent>
  </Card>
);

export default BlogCategoryPage;
